/**
 * Admin push-email composer.
 *
 * Lets the creator send a branded one-off email to a single subscriber
 * (or broadcast to all). Supports an optional embedded image, an
 * optional video link with poster, and the in-email PayPal CTA so
 * recipients can pay directly from the email.
 *
 * NEW: On open, automatically asks Workers AI to draft a subject + body
 * tailored to that recipient (their sub status, last-seen, churn risk).
 * Admin can pick a different tone or hit "Regenerate" for a fresh take.
 *
 * Calls POST /api/admin/push-email which is admin-gated server-side.
 * AI drafting calls POST /api/admin/ai/draft-push-email.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './PushEmailModal.css';
import { useModalA11y } from '../../hooks/useModalA11y';
import { adminEmailAPI, aiAPI, mediaAPI, PushEmailTone } from '../../services/api';
import { toast } from '../Toast/Toast';

export type PushEmailRecipient =
  | { kind: 'user'; userId: string; displayName?: string; email?: string }
  | { kind: 'all-active'; }
  | { kind: 'all-expired'; };

export interface PushEmailModalProps {
  recipient: PushEmailRecipient;
  onClose: () => void;
  onSent?: (result: { sent: number; failed: number; total: number }) => void;
}

const MAX_IMAGE_MB = 8;

const TONE_OPTIONS: Array<{ value: PushEmailTone; label: string }> = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'personal', label: 'Personal' },
  { value: 'bold',     label: 'Bold' },
  { value: 'urgent',   label: 'Urgent' },
  { value: 'flirty',   label: 'Flirty' },
];

const PushEmailModal: React.FC<PushEmailModalProps> = ({ recipient, onClose, onSent }) => {
  const dialogRef = useModalA11y({ onEscape: onClose });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [includeRenewBtn, setIncludeRenewBtn] = useState(true);
  const [renewAmount, setRenewAmount] = useState('25.00');
  const [renewTierLabel, setRenewTierLabel] = useState('Monthly');
  const [sending, setSending] = useState(false);

  // AI drafting state
  const [tone, setTone] = useState<PushEmailTone>('friendly');
  const [customAngle, setCustomAngle] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [aiUsed, setAiUsed] = useState(false);
  // Track if admin manually edited the subject/message so we don't overwrite their work
  // on tone-change auto-redraft. Reset whenever a draft is applied.
  const [dirtyAfterDraft, setDirtyAfterDraft] = useState(false);
  // Cancellation token for concurrent drafts (rapid tone clicks / regenerate)
  const draftRequestIdRef = useRef(0);

  // v2: the model's "read" of the subscriber (vibe / rapport / angle) +
  // the full set of 3 variants. The picker swaps subject + message to
  // the chosen variant. selectedVariantIdx starts at 0 since the
  // back-compat top-level subject/message already point to variants[0].
  const [aiRead, setAiRead] = useState<{ vibe: string; rapport: string; angle: string } | null>(null);
  const [variants, setVariants] = useState<Array<{ label: string; subject: string; preview: string; body: string }>>([]);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [readCollapsed, setReadCollapsed] = useState(false);

  const recipientUserId =
    recipient.kind === 'user' ? recipient.userId
    : recipient.kind === 'all-active' ? 'all-active'
    : 'all-expired';

  const recipientLabel =
    recipient.kind === 'user'
      ? (recipient.displayName || recipient.email || recipient.userId)
      : recipient.kind === 'all-active'
        ? 'All active subscribers (broadcast)'
        : 'All expired/cancelled (broadcast)';

  /* ------ AI draft ------------------------------------------------- */
  // Single source of truth for AI drafting. The caller can override `tone`
  // (used by tone-chip clicks to avoid stale-closure on `tone` state).
  // Concurrent calls are gated by `draftRequestIdRef` — only the newest
  // request can write back, so rapid clicks don't race.
  const runDraft = async (opts?: { silent?: boolean; toneOverride?: PushEmailTone }) => {
    const myId = ++draftRequestIdRef.current;
    setDrafting(true);
    setDraftError(null);
    try {
      const res = await aiAPI.draftPushEmail({
        recipientUserId,
        tone: opts?.toneOverride || tone,
        customAngle: customAngle.trim() || undefined,
        includeRenewCTA: includeRenewBtn,
        renewTierLabel,
        renewAmount,
      });
      // Stale-response guard.
      if (myId !== draftRequestIdRef.current) return;
      setSubject(res.subject);
      setMessage(res.message);
      // v2: surface the model's read + full variant list. Back-compat:
      // if the API didn't return these (old worker), leave them empty
      // and the UI hides the new sections.
      setAiRead(res.read || null);
      setVariants(Array.isArray(res.variants) ? res.variants : []);
      setSelectedVariantIdx(0);
      setReadCollapsed(false);
      setAiUsed(true);
      setDirtyAfterDraft(false);
      if (!opts?.silent) toast.success('Draft ready.');
    } catch (e: any) {
      if (myId !== draftRequestIdRef.current) return;
      console.error('[push-email] AI draft failed:', e);
      const msg = e?.message || 'AI draft failed';
      setDraftError(msg);
      if (!opts?.silent) toast.error(msg);
    } finally {
      if (myId === draftRequestIdRef.current) setDrafting(false);
    }
  };

  // Auto-draft once on open.
  useEffect(() => {
    runDraft({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToneChange = (next: PushEmailTone) => {
    setTone(next);
    // If admin hasn't manually edited the existing draft, refresh it with
    // the new tone. Otherwise preserve their work — they can hit
    // Regenerate explicitly when ready.
    if (!dirtyAfterDraft) {
      runDraft({ silent: true, toneOverride: next });
    }
  };

  const onSubjectChange = (v: string) => { setSubject(v); if (aiUsed) setDirtyAfterDraft(true); };
  const onMessageChange = (v: string) => { setMessage(v); if (aiUsed) setDirtyAfterDraft(true); };

  /* ------ Image upload --------------------------------------------- */
  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Only images are supported here.');
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(`Max ${MAX_IMAGE_MB} MB.`);
      return;
    }
    setUploading(true);
    try {
      const res = await mediaAPI.uploadFile(file);
      if (res?.url) setImageUrl(res.url);
    } catch (e: any) {
      toast.error(e?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  /* ------ Send ----------------------------------------------------- */
  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Subject and message are required.');
      return;
    }
    setSending(true);
    try {
      const res = await adminEmailAPI.push({
        recipientUserId,
        subject: subject.trim(),
        message: message.trim(),
        imageUrl: imageUrl || undefined,
        includeRenewButton: includeRenewBtn,
        renewAmount: includeRenewBtn ? renewAmount.trim() : undefined,
        renewTierLabel: includeRenewBtn ? renewTierLabel.trim() : undefined,
      });

      if (res?.success) {
        toast.success(`Sent to ${res.sent} of ${res.total}.`);
        onSent?.(res);
        onClose();
      } else {
        toast.error('Send failed.');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  return createPortal((
    <div className="modal-overlay push-email-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} ref={dialogRef} role="dialog" aria-modal="true" aria-label="Push email composer" tabIndex={-1}>
      <div className="push-email-modal">
        <div className="push-email-header">
          <h2>Push Email <span className="push-email-ai-badge" title="Auto-drafted by AI">AI</span></h2>
          <button type="button" className="push-email-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="push-email-recipient">
          <span className="push-email-label">To</span>
          <strong>{recipientLabel}</strong>
        </div>

        <div className="push-email-body">
          {/* AI controls */}
          <div className="push-email-ai-bar">
            <div className="push-email-ai-tones">
              {TONE_OPTIONS.map(t => (
                <button
                  key={t.value}
                  type="button"
                  className={`push-email-tone-chip ${tone === t.value ? 'is-active' : ''}`}
                  onClick={() => handleToneChange(t.value)}
                  disabled={drafting || sending}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="push-email-regen"
              onClick={() => runDraft()}
              disabled={drafting || sending}
              title="Regenerate the subject + message"
            >
              {drafting ? 'Drafting…' : '↻ Regenerate'}
            </button>
          </div>

          <label className="push-email-field">
            <span>Angle / hook (optional)</span>
            <input
              type="text"
              value={customAngle}
              onChange={e => setCustomAngle(e.target.value)}
              placeholder='e.g., "tease the upcoming drop" or "thank them for loyalty"'
              disabled={drafting || sending}
            />
          </label>

          {draftError && (
            <div className="push-email-draft-error">
              AI draft failed: {draftError}. You can write the email manually below.
            </div>
          )}

          {/* v2: AI subscriber read — surfaces WHY the model drafted what it did.
              Collapsible so it's quick to dismiss once the admin has skimmed it. */}
          {aiRead && (
            <div className="push-email-ai-read">
              <button
                type="button"
                className="push-email-ai-read-header"
                onClick={() => setReadCollapsed(c => !c)}
                aria-expanded={!readCollapsed}
              >
                <span className="push-email-ai-read-title">AI read</span>
                <span className="push-email-ai-read-caret" aria-hidden="true">{readCollapsed ? '▸' : '▾'}</span>
              </button>
              {!readCollapsed && (
                <dl className="push-email-ai-read-body">
                  <div><dt>Vibe</dt><dd>{aiRead.vibe}</dd></div>
                  <div><dt>Rapport</dt><dd>{aiRead.rapport}</dd></div>
                  <div><dt>Angle</dt><dd>{aiRead.angle}</dd></div>
                </dl>
              )}
            </div>
          )}

          {/* v2: variant picker — 3 chips, click to swap subject + message
              into that variant. Preview line shows in the tooltip-style row. */}
          {variants.length > 1 && (
            <div className="push-email-variants" role="tablist" aria-label="Draft variants">
              {variants.map((v, idx) => {
                const active = idx === selectedVariantIdx;
                return (
                  <button
                    key={idx}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`push-email-variant${active ? ' is-active' : ''}`}
                    onClick={() => {
                      setSelectedVariantIdx(idx);
                      setSubject(v.subject);
                      setMessage(v.body);
                      setDirtyAfterDraft(false);
                    }}
                    title={v.preview}
                  >
                    <span className="push-email-variant-label">{v.label}</span>
                    <span className="push-email-variant-subject">{v.subject}</span>
                  </button>
                );
              })}
            </div>
          )}

          <label className="push-email-field push-email-field-counter">
            <span>
              Subject
              {drafting && <em className="push-email-drafting-flag">drafting…</em>}
              <span
                className={`push-email-char-count${subject.length > 120 ? ' is-warn' : ''}${subject.length >= 140 ? ' is-cap' : ''}`}
                title="Most inboxes truncate the subject around 60–80 characters; 140 is the hard cap."
              >
                {subject.length}/140
              </span>
            </span>
            <input
              type="text"
              value={subject}
              onChange={e => onSubjectChange(e.target.value)}
              maxLength={140}
              placeholder={drafting ? 'AI is drafting…' : 'A message from Jalen'}
              disabled={drafting}
            />
          </label>

          <label className="push-email-field">
            <span>Message</span>
            <textarea
              value={message}
              onChange={e => onMessageChange(e.target.value)}
              rows={7}
              placeholder={drafting ? 'AI is drafting your message…' : 'Write your message here. Blank lines create paragraphs.'}
              disabled={drafting}
            />
          </label>

          <div className="push-email-field">
            <span>Attach image (optional)</span>
            {imageUrl ? (
              <div className="push-email-image-preview">
                <img
                  src={imageUrl}
                  alt="Attachment preview"
                  onError={() => {
                    toast.error('Attachment image failed to load — re-upload?');
                    setImageUrl(null);
                  }}
                />
                <button type="button" onClick={() => setImageUrl(null)} className="push-email-remove-image">Remove</button>
              </div>
            ) : (
              <button
                type="button"
                className="push-email-upload"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading…' : '+ Add image (up to 8 MB)'}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
          </div>

          <label className="push-email-checkbox">
            <input
              type="checkbox"
              checked={includeRenewBtn}
              onChange={e => setIncludeRenewBtn(e.target.checked)}
            />
            <span>Include in-email PayPal renew button</span>
          </label>

          {includeRenewBtn && (
            <div className="push-email-renew-row">
              <label className="push-email-field push-email-field-narrow">
                <span>Tier label</span>
                <input
                  type="text"
                  value={renewTierLabel}
                  onChange={e => setRenewTierLabel(e.target.value)}
                  placeholder="Monthly"
                />
              </label>
              <label className="push-email-field push-email-field-narrow">
                <span>Amount (USD)</span>
                <input
                  type="text"
                  value={renewAmount}
                  onChange={e => setRenewAmount(e.target.value)}
                  placeholder="25.00"
                />
              </label>
            </div>
          )}
        </div>

        <div className="push-email-footer">
          <button type="button" className="push-email-cancel" onClick={onClose} disabled={sending}>Cancel</button>
          <button
            type="button"
            className="push-email-send"
            onClick={handleSend}
            disabled={sending || uploading || drafting || !subject.trim() || !message.trim()}
          >
            {sending ? 'Sending…' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};

export default PushEmailModal;
